import { FrameProfile, ScenePresetId, StudioRoomState, WallSide } from './types'

/**
 * New, isolated note schema for this experimental Studio — deliberately NOT
 * added to the shared `PatternNoteTypes`/`NoteType` enums in
 * `src/utils/constants.ts` (noteType '2'..'19' are all reserved by the
 * legacy pattern/gallery system). '90' is unused by either enum.
 */
export const STUDIO3D_NOTE_TYPE = '90'
export const STUDIO3D_NOTE_NAME = 'studio3dSceneStateV1'

/**
 * Story pins — each pin is its own Note on the gallery topic (same
 * `topicNoteUpdate` add/update/delete storage as everything else), not a
 * field inside the `studio3dSceneStateV1` blob. See
 * `issues/docs/story-layer-album-comment-bridge-notes.md` for why: one
 * shared JSON blob meant every pin edit was a full-note overwrite race.
 */
export const STUDIO_PIN_NOTE_TYPE = '91'
export const STUDIO_PIN_NOTE_NAME = 'studioPin'

export const ROOM_DEFAULTS: StudioRoomState = {
  width: 12,
  height: 5,
  depth: 16,
  wallColor: '#40365a',
  floorColor: '#2c2340',
  ceilColor: '#1d1729',
  ambience: 0.82,
  fog: 0.008,
  dwellSeconds: 3,
  showPins: true,
  showPresenceDemo: false,
  useHdri: false,
  scenePreset: 'corridor',
  mode: 'studio',
}

/**
 * Room shell geometry + default finishes per scene preset — a rough "mock"
 * pass so Loft/Pavilion can actually be previewed (different footprint,
 * ceiling height, wall/floor tone, and light color temperature), rather than
 * only existing as PRO-gated labels. Swapping presets rebuilds the room shell
 * and resets wall/floor color to the new preset's defaults; it does NOT
 * reflow already-hung prints (`art.u`/`art.y` are absolute meters from
 * room center) — some prints may sit close to or past a smaller room's walls
 * after a swap. Good enough to evaluate the look/feel; not production-polish.
 */
export interface ScenePresetGeometry {
  label: string
  width: number
  height: number
  depth: number
  wallColor: string
  floorColor: string
  /** Floor specular highlight tint — was hardcoded violet-gray for all three, wrong on warm/pale floors. */
  floorSpecular: string
  /** Ceiling plane color — distinct per preset (was hardcoded dark-violet for all three). */
  ceilColor: string
  /** Floor/wall-junction strip-light emissive tint. */
  stripColor: string
  /** Hemispheric light diffuse/ground tint — sets the room's color temperature. */
  ambientDiffuse: string
  ambientGround: string
  /** Hemispheric light intensity — reset on preset swap, not carried over, so each scene has its own mood. */
  ambience: number
  /** Point-light fill tint. */
  fillColor: string
  /** Ceiling lamp emissive tint. */
  lampColor: string
  /** Exponential fog density — reset on preset swap along with ambience. */
  fog: number
  /** Extra structural dressing unique to this preset — see `buildStructure` in useStudioRoom. */
  structure: 'lamps' | 'columns' | 'skylight'
}

export const SCENE_PRESET_GEOMETRY: Record<ScenePresetId, ScenePresetGeometry> = {
  corridor: {
    label: 'Corridor',
    width: 12,
    height: 5,
    depth: 16,
    wallColor: '#40365a',
    floorColor: '#2c2340',
    floorSpecular: '#4a3d63',
    ceilColor: '#1d1729',
    stripColor: '#4a3a20',
    ambientDiffuse: '#c3b3de',
    ambientGround: '#443355',
    ambience: 0.82,
    fillColor: '#8d7ab0',
    lampColor: '#f0d9ac',
    fog: 0.008,
    structure: 'lamps',
  },
  loft: {
    label: 'Loft',
    width: 16,
    height: 4.2,
    depth: 14,
    // Match WALL_TONES / FLOOR_TONES so the inspector swatch stays selected
    // after a preset swap (orphan hexes looked "stuck" on Corridor purple).
    wallColor: '#b06a48',
    floorColor: '#5a4634',
    floorSpecular: '#3a2a1a',
    ceilColor: '#241a12',
    stripColor: '#3a2c18',
    ambientDiffuse: '#e0c9a6',
    ambientGround: '#3a2f24',
    ambience: 0.62,
    fillColor: '#c98f52',
    lampColor: '#ffcf7d',
    fog: 0.014,
    structure: 'columns',
  },
  pavilion: {
    label: 'Pavilion',
    width: 20,
    height: 6.5,
    depth: 20,
    // Pale, but NOT near-white — at this room's ambience the pipeline's
    // exposure (1.5) + contrast (1.07) will blow near-white materials out to
    // a flat, featureless void with no visible wall/floor/ceiling edges.
    wallColor: '#d9d4c6',
    floorColor: '#b6ac97',
    floorSpecular: '#dcd6c8',
    ceilColor: '#d7dcdb',
    stripColor: '#c3d2d5',
    ambientDiffuse: '#f0ede5',
    ambientGround: '#a8aeab',
    ambience: 0.68,
    fillColor: '#bfd8e0',
    lampColor: '#fff8ea',
    fog: 0.002,
    structure: 'skylight',
  },
}

export const FRAME_PROFILES: Record<FrameProfile, { border: number; depth: number }> = {
  slim: { border: 0.05, depth: 0.05 },
  gallery: { border: 0.11, depth: 0.09 },
  deep: { border: 0.18, depth: 0.16 },
  float: { border: 0.03, depth: 0.13 },
}

export const FRAME_PROFILE_OPTIONS: { value: FrameProfile; label: string }[] = [
  { value: 'slim', label: 'Slim' },
  { value: 'gallery', label: 'Gallery' },
  { value: 'deep', label: 'Deep' },
  { value: 'float', label: 'Float' },
]

export const FRAME_FINISHES: { name: string; hex: string }[] = [
  { name: 'Noir', hex: '#0d0b10' },
  { name: 'Walnut', hex: '#5a4030' },
  { name: 'Brass', hex: '#8a6c3c' },
  { name: 'Ivory', hex: '#e8e2d4' },
  { name: 'Chalk', hex: '#f5f5f2' },
]

export const MAT_TONES: { name: string; hex: string }[] = [
  { name: 'Ivory', hex: '#efe9dd' },
  { name: 'Chalk', hex: '#f6f4ef' },
  { name: 'Charcoal', hex: '#2b2830' },
  { name: 'Blush', hex: '#e9d5cd' },
]

/**
 * Scene presets — 1:1 with the mockup's SCENE segmented control. In the
 * mockup, Loft/Pavilion are pure PRO-gated stubs (toast only, no real room).
 * Here they're wired to real (if roughly-mocked) room shells via
 * `SCENE_PRESET_GEOMETRY` so they can actually be previewed, gated behind
 * `FEATURE_KEYS.STUDIO_PREMIUM_SCENES` on selection. As of task #17
 * (Classic-first launch default), this whole picker is also only reachable
 * for a gallery already saved in Studio mode — see `RoomInspector`'s mode
 * tabs and `gallery-studio-3d/FLOW.md`'s "Classic-first launch default"
 * section.
 */
export const SCENE_OPTIONS: { value: string; label: string; pro?: boolean }[] = [
  { value: 'corridor', label: 'Corridor' },
  { value: 'loft', label: 'Loft', pro: true },
  { value: 'pavilion', label: 'Pavilion', pro: true },
]

export const WALL_TONES = ['#40365a', '#1b1722', '#efece4', '#9aa88f', '#b06a48', '#2a3350']
export const FLOOR_TONES = ['#241c33', '#5a4634', '#cfc8bb', '#6e6e6c']
/** Stock ceilColor of each preset (corridor/loft/pavilion) plus a near-black and a cool violet-gray alternate. */
export const CEIL_TONES = ['#1d1729', '#241a12', '#d7dcdb', '#0f0e14', '#3a3550']

/** Signature spectrum stops, reused as the studio's dark-theme accent palette. */
export const STUDIO_COLORS = {
  gold: '#ffcf7d',
  coral: '#fea67d',
  violet: '#be9dfd',
  cyan: '#01dafe',
}

export const WALL_NAME: Record<WallSide, string> = {
  [-1]: 'LEFT WALL',
  0: 'BACK WALL',
  1: 'RIGHT WALL',
}

export const WALL_MESH_NAME: Record<WallSide, string> = {
  [-1]: 'wall_left',
  0: 'wall_back',
  1: 'wall_right',
}

export const HOME_CAMERA = {
  alpha: -1.08,
  beta: 1.35,
  radius: 9.2,
  target: { x: -0.6, y: 1.9, z: 1.4 },
}

/**
 * Orbit `alpha` range for the bottom-right "look left/right" sweep slider
 * (see `StudioOrbitSlider`) — `min` faces the left wall, `max` faces the
 * right wall, matching `flyTo`'s own left/right wall-facing convention in
 * `useStudioCameraRig` (`target.side === -1 ? 0 : target.side === 1 ?
 * Math.PI : ...`). Shared here so the rig and the slider never drift apart.
 */
export const ORBIT_SWEEP_ALPHA = { min: 0, max: Math.PI }

export const DEFAULT_ART_DEFAULTS = {
  ratio: 4 / 3,
  w: 1.8,
  profile: 'gallery' as FrameProfile,
  finish: '#0d0b10',
  matW: 0.07,
  matTone: '#efe9dd',
  spotI: 9,
  warm: 0.7,
}
